/** Card catalog types. The catalog itself lives in the DB (seeded from data/cards.json) so Ben can add cards in Admin. */
export type CardType = 'fighter' | 'item' | 'corrupted' | 'gooflord';
export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';
export type ItemTarget = 'ally' | 'enemy';
/** Which printed set a card belongs to. Cards without one are the original set. Each set has its own pack. */
export type CardSet = 'og' | 'sunset';
export const CARD_SETS: CardSet[] = ['og', 'sunset'];

export interface Card {
  slug: string;
  name: string;
  type: CardType;
  level: number;
  rarity: Rarity;
  /** Ability key the engine runs (items, corrupted, gooflords). Fighters have none. */
  ability?: string;
  text?: string;
  flavor?: string;
  target?: ItemTarget;
  value?: number;
  threshold?: number;
  fail?: number;
  on_death?: 'draw_fighter' | 'draw_items';
  /** URL of the scan, or null when the card has no art yet. */
  art?: string | null;
  inactive?: boolean;
  physical?: boolean;
  /** printed set; missing means 'og' */
  set?: CardSet;
  /** Ben's sun symbol: the card has an ability its owner taps to use while it's in the party */
  sun?: boolean;
  /** copies allowed in one deck when lower than DECK_RULES.maxCopies (only matters if the deck rules ever allow more than 1) */
  maxCopies?: number;
  emoji?: string;
  hue?: number;
}

export type Catalog = Record<string, Card>;

export const toCatalog = (cards: Card[]): Catalog => {
  const cat: Catalog = {};
  for (const c of cards) cat[c.slug] = c;
  return cat;
};

export const RARITY_WEIGHTS: Record<Rarity, number> = { common: 60, rare: 28, epic: 10, legendary: 2 };
export const RARITIES: Rarity[] = ['common', 'rare', 'epic', 'legendary'];
export const CARD_TYPES: CardType[] = ['fighter', 'item', 'corrupted', 'gooflord'];

/** Ability keys the engine understands, so Admin can validate a new card before saving it. */
export const GOOFLORD_ABILITIES = [
  'jimbo', 'blob_bear', 'pip', 'king_plum', 'tooth_catcher', 'ducky', 'cluck', 'bog', 'party_dice',
  'key_master', 'king_fish', 'chunky', 'bubble', 'acidity', 'grumpy',
] as const;
export const ITEM_ABILITIES = [
  'buff', 'buff_draw', 'd20', 'insure', 'stun', 'debuff_target', 'kill', 'draw_items', 'shuffle_draw_fighter', 'draw_bottom',
  'everyone_draw_fighter', 'block_items', 'scry', 'discard_enemy_fighter', 'd20_discard_fighter', 'skip_turn', 'recover_item', 'egg',
  'discard_enemy_items', 'summon', 'roll_bonus', 'd20_revive_corrupted',
  'steal_fighter', 'steal_corrupted', 'dumbo', 'peek_items', 'peek_fighters', 'five_eyes', 'anihilate', 'deadly_wish', 'draw_stun',
] as const;
export const CORRUPTED_ABILITIES = ['corr_chicken', 'corr_daisy', 'corr_blobber', 'corr_bobfish', 'corr_king_eye', 'corr_beoteo', 'corr_booger', 'corr_apbf'] as const;
/** Corrupted abilities the owner taps to use (once each time the card enters a party). Daisy's and BeoTeo's are always on. */
export const CORRUPTED_ACTIVE = ['corr_chicken', 'corr_blobber', 'corr_bobfish', 'corr_king_eye', 'corr_booger', 'corr_apbf'] as const;
/** Sun-symbol fighters (Sunset's Secret Fighters): tapped like a Corrupted ability, once each time the card enters a party. */
export const SUN_ABILITIES = ['sun_cultist', 'sun_toad', 'sun_shadow', 'sun_siren', 'sun_gloopy', 'sun_jerry'] as const;
/** Every ability used with the ⚡/☀️ button on a party fighter. */
export const TAP_ABILITIES: readonly string[] = [...CORRUPTED_ACTIVE, ...SUN_ABILITIES];
/**
 * What a tapped (or Gooflord) ability asks its owner to pick, in order. `enemy`: an enemy fighter (targetPlayer + target);
 * `enemy_ally`: another fighter in that same enemy party (picks); `hand`: an item in your hand (picks); `ally`: one of
 * your fighters (picks, or target for a single one).
 */
export type PickStep = 'enemy' | 'enemy_ally' | 'hand' | 'ally';
export const ABILITY_PICKS: Record<string, PickStep[]> = {
  sun_cultist: ['enemy'], sun_shadow: ['enemy', 'enemy_ally'], sun_toad: ['hand', 'hand'], chunky: ['ally', 'ally'],
};
export const ALL_ABILITIES: readonly string[] = [...GOOFLORD_ABILITIES, ...ITEM_ABILITIES, ...CORRUPTED_ABILITIES, ...SUN_ABILITIES];
/** The set a card belongs to (missing = the original set). */
export const cardSet = (c: Pick<Card, 'set'>): CardSet => c.set ?? 'og';
